import { useCallback, useState } from "react";
import { useTouchscreenContext, useBrand } from "@touchscreen/hooks";
import { useBrandModule } from "@touchscreen/brand-kit";
import { AttractScreen } from "./screens/AttractScreen.js";
import { BrowseScreen } from "./screens/BrowseScreen.js";
import { SearchScreen } from "./screens/SearchScreen.js";
import { HoldingScreen } from "./components/HoldingScreen.js";
import { BuildBadge } from "./components/BuildBadge.js";
import { IdleTimeoutBar } from "./components/IdleTimeoutBar.js";
import { useIdleReturn } from "./lib/useIdleReturn.js";

type View = "attract" | "browse" | "search";

const IDLE_TIMEOUT_MS = 30_000;
const IDLE_PULSE_MS = 5_000;

export function KioskShell(): React.JSX.Element {
  const { boot } = useTouchscreenContext();
  const { brandKey } = useBrand();
  useBrandModule(brandKey); // applies brand CSS custom properties as a side effect

  const [view, setView] = useState<View>("attract");

  const returnToAttract = useCallback(() => setView("attract"), []);
  const idleResetCount = useIdleReturn(IDLE_TIMEOUT_MS, returnToAttract, view !== "attract");

  if (boot.status === "booting") {
    return <HoldingScreen message="Loading" />;
  }

  if (boot.status === "error") {
    // The provider already retries transparently on token_expired; anything surfacing here is
    // a real failure to reach the API. Stay neutral — this is a screen nobody is standing in front of.
    return <HoldingScreen message="One moment" detail="Reconnecting…" />;
  }

  return (
    <>
      {view === "attract" ? <AttractScreen onTouch={() => setView("browse")} /> : null}
      {view === "browse" ? (
        <BrowseScreen onBack={returnToAttract} onSearch={() => setView("search")} onRequested={returnToAttract} />
      ) : null}
      {view === "search" ? (
        <SearchScreen onBack={() => setView("browse")} onRequested={returnToAttract} />
      ) : null}
      {view !== "attract" ? (
        <IdleTimeoutBar key={idleResetCount} timeoutMs={IDLE_TIMEOUT_MS} pulseMs={IDLE_PULSE_MS} />
      ) : null}
      <BuildBadge />
    </>
  );
}
